class OutletProduct extends HTMLElement {
  connectedCallback() {
    this.cards = Array.from(this.querySelectorAll('[data-outlet-offer]'));
    this.form = this.querySelector('[data-outlet-form]');
    this.quantity = this.querySelector('[data-outlet-quantity]');
    this.variantInput = this.querySelector('[data-outlet-variant]');
    this.offerInput = this.querySelector('[data-outlet-offer-id]');
    this.sizeInput = this.querySelector('[data-outlet-size-input]');
    this.materialInput = this.querySelector('[data-outlet-material-input]');
    this.conditionInput = this.querySelector('[data-outlet-condition-input]');
    this.packageInput = this.querySelector('[data-outlet-package-input]');
    this.fullLotInput = this.querySelector('[data-outlet-full-lot-input]');
    this.detailsRoot = this.closest('product-info')?.querySelector('[data-outlet-details]');

    this.cards.forEach((card) => {
      card.addEventListener('click', () => this.select(card));
      card.querySelector('input')?.addEventListener('change', () => this.select(card));
    });

    this.querySelector('[data-outlet-minus]')?.addEventListener('click', () => this.stepQuantity(-1));
    this.querySelector('[data-outlet-plus]')?.addEventListener('click', () => this.stepQuantity(1));
    this.querySelector('[data-outlet-buy-all]')?.addEventListener('click', () => {
      if (this.quantity) this.quantity.value = this.quantity.max || 1;
      this.syncLotState(true);
    });
    this.quantity?.addEventListener('input', () => this.syncLotState());
    this.quantity?.addEventListener('change', () => this.syncLotState());
    this.initDetailsTabs();

    if (this.dataset.demo === 'true') {
      this.form?.addEventListener('submit', (event) => event.preventDefault());
    }

    const initial = this.cards.find((card) => card.classList.contains('is-selected')) || this.cards[0];
    if (initial) this.select(initial);
  }

  select(card) {
    this.selectedCard = card;
    this.cards.forEach((item) => item.classList.toggle('is-selected', item === card));
    const radio = card.querySelector('input');
    if (radio) radio.checked = true;

    const available = Math.max(1, Number(card.dataset.available || 1));
    const physicalQuantity = Math.max(1, Number(card.dataset.physicalQuantity || available));
    const isLotOnly = card.dataset.sellMode === 'lot_only';
    const unitSingular = card.dataset.unitSingular || 'piece';
    const unitPlural = card.dataset.unitPlural || 'pieces';
    const availableUnit = available === 1 ? unitSingular : unitPlural;
    const packageLabel = isLotOnly ? `Set of ${physicalQuantity} ${unitPlural}` : '';
    const setText = (selector, value) => {
      const node = this.querySelector(selector);
      if (node) node.textContent = value || '—';
    };

    setText('[data-selected-size]', card.dataset.size);
    setText('[data-selected-material]', card.dataset.material);
    setText('[data-selected-condition]', card.dataset.condition);
    setText(
      '[data-selected-available]',
      isLotOnly
        ? `1 set available · ${physicalQuantity} ${unitPlural} included`
        : `${available} ${availableUnit} available`
    );
    setText('[data-selected-package]', packageLabel);
    setText('[data-selected-price]', card.dataset.price);
    setText('[data-selected-saving]', card.dataset.saving);

    const compare = this.querySelector('[data-selected-compare]');
    if (compare) {
      compare.textContent = card.dataset.compare || '';
      compare.hidden = !card.dataset.compare;
    }

    const saving = this.querySelector('[data-selected-saving]');
    if (saving) saving.hidden = !card.dataset.saving;

    if (this.variantInput) this.variantInput.value = card.dataset.variant || '';
    if (this.offerInput) this.offerInput.value = card.dataset.offerId || '';
    if (this.sizeInput) this.sizeInput.value = card.dataset.sourceSize || card.dataset.size || '';
    if (this.materialInput) this.materialInput.value = card.dataset.material || '';
    if (this.conditionInput) this.conditionInput.value = card.dataset.condition || '';
    if (this.packageInput) this.packageInput.value = packageLabel;

    const packageRow = this.querySelector('[data-selected-package-row]');
    if (packageRow) packageRow.hidden = !isLotOnly;

    if (this.quantity) {
      this.quantity.max = available;
      this.quantity.value = 1;
    }

    const quantityControl = this.querySelector('[data-outlet-quantity-control]');
    if (quantityControl) quantityControl.hidden = isLotOnly;
    this.querySelector('.outlet-purchase__controls')?.classList.toggle('is-lot-only', isLotOnly);

    const lotDeal = this.querySelector('[data-outlet-buy-all]');
    if (lotDeal) {
      lotDeal.hidden = available <= 1 || !card.dataset.lotPrice;
      lotDeal.classList.remove('is-active');
      lotDeal.setAttribute('aria-pressed', 'false');
      setText('[data-lot-quantity]', available);
      setText('[data-lot-price-output]', card.dataset.lotPrice);
      setText('[data-lot-unit-price-output]', card.dataset.lotUnitPrice ? `${card.dataset.lotUnitPrice} each` : '');
      setText('[data-lot-saving-output]', card.dataset.lotSaving);
    }

    this.syncLotState();
    this.syncMaterialDetails(card.dataset.material);
  }

  initDetailsTabs() {
    if (!this.detailsRoot) return;
    const buttons = Array.from(this.detailsRoot.querySelectorAll('[data-outlet-details-tab]'));
    const panels = Array.from(this.detailsRoot.querySelectorAll('[data-outlet-details-panel]'));

    const activate = (name, moveFocus = false) => {
      buttons.forEach((button) => {
        const active = button.dataset.outletDetailsTab === name;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', active ? 'true' : 'false');
        button.tabIndex = active ? 0 : -1;
        if (active && moveFocus) button.focus();
      });
      panels.forEach((panel) => {
        const active = panel.dataset.outletDetailsPanel === name;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
      });
    };

    buttons.forEach((button, index) => {
      button.addEventListener('click', () => activate(button.dataset.outletDetailsTab));
      button.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        const nextIndex = (index + direction + buttons.length) % buttons.length;
        activate(buttons[nextIndex].dataset.outletDetailsTab, true);
      });
    });
  }

  syncMaterialDetails(material) {
    if (!this.detailsRoot || !material) return;
    const selectedMaterial = material.trim().toLowerCase();
    const panels = Array.from(this.detailsRoot.querySelectorAll('[data-outlet-material-panel]'));
    let matched = false;

    panels.forEach((panel) => {
      const active = !matched && (panel.dataset.materialName || '').trim().toLowerCase() === selectedMaterial;
      panel.hidden = !active;
      if (active) matched = true;
    });

    if (!matched && panels[0]) panels[0].hidden = false;
  }

  stepQuantity(delta) {
    if (!this.quantity) return;
    const min = Number(this.quantity.min || 1);
    const max = Number(this.quantity.max || 1);
    const current = Number(this.quantity.value || min);
    this.quantity.value = Math.min(max, Math.max(min, current + delta));
    this.syncLotState();
  }

  syncLotState(forceActive = false) {
    if (!this.quantity || !this.selectedCard) return;
    const lotDeal = this.querySelector('[data-outlet-buy-all]');
    const max = Number(this.quantity.max || 1);
    const current = Number(this.quantity.value || 1);
    const hasLotDeal = max > 1 && Boolean(this.selectedCard.dataset.lotPrice);
    const isActive = hasLotDeal && (forceActive || current === max);

    lotDeal?.classList.toggle('is-active', isActive);
    lotDeal?.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    const fullLotInput = this.fullLotInput || this.querySelector('[data-outlet-full-lot-input]');
    if (fullLotInput) {
      const value = isActive ? 'yes' : 'no';
      fullLotInput.value = value;
      fullLotInput.setAttribute('value', value);
    }
  }
}

if (!customElements.get('outlet-product')) {
  customElements.define('outlet-product', OutletProduct);
}
